import React from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import i18n from 'i18n';
import Switch from '@material-ui/core/Switch';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n } from 'utils/helpers';
import { ClientFields } from 'modules/clients/interfaces';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core';
import styles from './styles';
import { screenFields, SSOFieldsEnum } from './formFields';
import BoxSection from './BoxSection';

const useStyles = makeStyles(styles);

export interface ClientSSOConfigProps {
  setFieldValue: (key: string, value: unknown) => void;
  errors: any;
  clientSelected: ClientFields;
}

const ClientSSOConfig: React.FC<ClientSSOConfigProps> = ({
  setFieldValue,
  errors,
  clientSelected,
}) => {
  const classes = useStyles();

  const restrictAccessGroupsValue = React.useMemo(
    () => ({
      sso: {
        ...clientSelected?.sso,
        // eslint-disable-next-line @typescript-eslint/camelcase
        restrict_access_by_groups: !clientSelected?.sso
          ? 0
          : Number(clientSelected?.sso?.restrict_access_by_groups),
      },
    }),
    [clientSelected]
  );

  const restrictedAccessFields = React.useMemo(() => {
    const limitSlice =
      restrictAccessGroupsValue?.sso?.restrict_access_by_groups === 0 ? 1 : 2;

    return screenFields[SSOFieldsEnum.samlAccess]?.slice(0, limitSlice);
  }, [restrictAccessGroupsValue]);

  const samlAccessHandleChange = (key: string, value: unknown) => {
    if (key?.includes('restrict_access_by_groups')) {
      setFieldValue(key, Boolean(value));
    } else {
      setFieldValue(key, value);
    }
  };

  return (
    <>
      <Grid container data-testid={'autosky-clientssoconfig'}>
        <Typography className={classes.sectionTitle} variant="h4">
          {i18n.t('SSO_KEY_LONG')}
        </Typography>
      </Grid>
      <Grid container className={classes.virtualizerSwitch}>
        <FormControlLabel
          className={classes.formLabel}
          control={
            <Switch
              id="toggle-use-environment-virtualizer"
              classes={{ colorSecondary: classes.switchChecked }}
              size="small"
              onChange={(_, checked) => {
                setFieldValue('sso_enabled', checked);
              }}
              checked={clientSelected?.sso_enabled}
              name="virtualizer"
            />
          }
          label={
            <Typography className={classes.switchText}>
              {i18n.t('ACTIVE_SSO_DESCRIPTION')}
            </Typography>
          }
        />
      </Grid>
      {clientSelected?.sso_enabled === true && (
        <Grid container className={classes.ssoContainer}>
          <Grid container spacing={2}>
            <GenericForm
              overrideClasses={{
                selectLabel: { fontSize: 12, fontWeight: 500 },
              }}
              fields={clientSelected}
              screenFields={applyI18n(
                screenFields[SSOFieldsEnum.header],
                i18n.language
              )}
              setFieldValue={(a, b) => {
                setFieldValue(a, b);
              }}
              changeField={({ key, value }) => {
                setFieldValue(key, value);
              }}
              errors={errors}
            />
          </Grid>

          <BoxSection
            title={i18n.t('SSO.SAML_URL_SECTION_TITLE')}
            form={
              <GenericForm
                fields={clientSelected}
                screenFields={applyI18n(
                  screenFields[SSOFieldsEnum.samlUrl],
                  i18n.language
                )}
                setFieldValue={(a, b) => {
                  setFieldValue(a, b);
                }}
                changeField={({ key, value }) => {
                  setFieldValue(key, value);
                }}
                errors={errors}
              />
            }
          />

          <BoxSection
            title={i18n.t('SSO.SAML_ACCESS_TITLE')}
            form={
              <GenericForm
                overrideClasses={{
                  selectLabel: { fontSize: 12, fontWeight: 500 },
                }}
                fields={restrictAccessGroupsValue}
                screenFields={applyI18n(restrictedAccessFields, i18n.language)}
                setFieldValue={(a, b) => samlAccessHandleChange(a, b)}
                changeField={({ key, value }) =>
                  samlAccessHandleChange(key, value)
                }
                errors={errors}
              />
            }
          />

          <BoxSection
            title={i18n.t('SSO.SAML_CLAIM_TYPES_TITLE')}
            form={
              <GenericForm
                overrideClasses={{
                  selectLabel: { fontSize: 12, fontWeight: 500 },
                }}
                fields={clientSelected}
                screenFields={applyI18n(
                  screenFields[SSOFieldsEnum.samlUser],
                  i18n.language
                )}
                setFieldValue={(a, b) => {
                  setFieldValue(a, b);
                }}
                changeField={({ key, value }) => {
                  setFieldValue(key, value);
                }}
                errors={errors}
              />
            }
          />
        </Grid>
      )}
    </>
  );
};

export default ClientSSOConfig;
